// @file List of Tldraw React hooks we want to expose to Vue.
// Follow STEP 1 to STEP 3 below if you want to expose a new Tldraw hook to Vue.
import { STATIC_TLDRAWAI_OPTIONS } from '@@/bits/tldraw/static_tldraw_ai_options'
import { useTldrawCollaborationStatus } from '@@/reacthooks/useTldrawCollaborationStatus'
import { useTldrawAi as useTldrawAiBase } from '@tldraw/ai'
import { useEffect } from 'react'
import { useMenuClipboardEvents } from 'tldraw'

type AnyFunction = (...args: any[]) => any

/**
 * Utility to manage Tldraw hooks and their return values.
 */
const TldrawHook = (() => {
  const hooks = new Map<AnyFunction, ReturnType<AnyFunction>>()

  /**
   * Saves the return value of a Tldraw hook.
   */
  const set = <T extends AnyFunction>(hook: T, returnValue: ReturnType<T>): void => {
    hooks.set(hook, returnValue)
  }

  /**
   * Clears the list of hooks.
   */
  const clear = (): void => {
    hooks.clear()
  }

  /**
   * Gets a function for the given hook that can be called outside React.
   * The function can be undefined if the hook is not yet available.
   */
  const get = <T extends AnyFunction>(hook: T): T | undefined => {
    return (() => hooks.get(hook)) as T | undefined
  }

  /**
   * Gets a function for the given hook that can be called outside React. Forces its
   * type to always be non-undefined.
   *
   * This is useful when we want to fail loudly if the hook is used wrongly (e.g.
   * used outside of a Tldraw context, or used before the Tldraw editor is mounted).
   */
  const getUnsafe = <T extends AnyFunction>(hook: T): T => {
    return (() => hooks.get(hook)) as T
  }

  return { set, clear, get, getUnsafe }
})()

/**
 * Sets up Tldraw hooks we want to expose to Vue and track their return values.
 */
const SetupHooks = (): React.JSX.Element => {
  useTldrawCollaborationStatus()

  // #region STEP 1: Call the hooks.
  const menuClipboardEvents = useMenuClipboardEvents()
  const tldrawAi = useTldrawAiBase({ ...STATIC_TLDRAWAI_OPTIONS })
  // ... add more hooks as needed.
  // #endregion

  // #region STEP 2: Save the hooks' return values.
  useEffect(() => {
    TldrawHook.set(useMenuClipboardEvents, menuClipboardEvents)
    TldrawHook.set(useTldrawAiBase, tldrawAi)
    // ... add more hooks as needed.
    // Remember to update the dependency array accordingly.
  }, [menuClipboardEvents, tldrawAi])
  // #endregion

  // Clean up the hooks when the component unmounts.
  useEffect(() => {
    return () => TldrawHook.clear()
  }, [])

  return <></>
}

// #region STEP 3: Expose the hooks to Vue.
const useTldrawMenuClipboardEvents = TldrawHook.getUnsafe(useMenuClipboardEvents)
const useTldrawAi = TldrawHook.getUnsafe(useTldrawAiBase)
// ... add more hooks as needed.
// Choose `get` or `getUnsafe` based on your needs.
// - If using `get`, the hook can be called like this:
//   `useTldrawMenuClipboardEvents()?.copy()`
// - If using `getUnsafe`, the hook can be called like this:
//   `useTldrawMenuClipboardEvents().copy()`
//   but it will throw an error if the hook is not available.
// #endregion

export { SetupHooks, useTldrawAi, useTldrawMenuClipboardEvents }
